import { type Ref, watch } from "vue"
import { type IBase } from "@/interfaces/api/base.interface"
import SocketService from "@/services/socket/socket-service"
import type { BaseModel } from "@/models/base.model"
import ModelManager from "@/services/model-manager"
import { storeToRefs } from "pinia"
import { RoleTypes } from "@/enums/role-types"
import { useAuthStore } from "@/store/auth.store"
import { type ModelConstructorSignature } from "@/models/types/model-constructor-signature"

export function useSocketSynchronizationHandlers<
  ModelType extends BaseModel,
  DataType extends IBase
>(
  modelType: ModelConstructorSignature<DataType, ModelType>,
  modelCollection?: Ref<BaseModel[]> | null, // Collection of models delete from or push to.
  onModelCreate?: (model: ModelType) => void, // Create handler
  onModelDelete?: (model: ModelType) => void, // Delete handler

  // Ignore socket messages that were caused by this session.
  ignoreMessagesFromSelf: boolean = true
) {
  let initiated = false
  const authStore = useAuthStore()
  const { activeUser } = storeToRefs(authStore)
  watch(
    activeUser,
    (val) => {
      if (val && !initiated) {
        if (!val.knownBusinessEntityContact?.hasRole(RoleTypes.PLANNER)) {
          return // Currently, only planners can access the socket service.
        }

        initiate()
        initiated = true
      }
    },
    { immediate: true }
  )

  function initiate() {
    const socketService = SocketService.getInstance()

    if (!modelType.className) {
      throw Error("ModelType must have a className property")
    }

    if (ignoreMessagesFromSelf) {
      socketService?.ignoreSelfMessagesForModel(modelType.className)
    }

    /* Add handlers */
    socketService?.addModelCreateHandler({
      model: modelType.className,
      handler: async (data: IBase) => {
        if (!ModelManager.getInstance().exists(data.id!)) {
          // Check if the model was created locally.
          const instance = ModelManager.getInstance().create(modelType, data as DataType)
          if (instance === null) {
            return // Could be null if the model was deleted locally.
          }
          handleCreate(instance)
        }
      }
    })

    socketService?.addModelUpdateHandler({
      model: modelType.className,
      handler: async (data: IBase) => {
        const oldValue = ModelManager.getInstance().get(data.id!)
        // Only update if...
        // * the data is newer than the current data. In this case we obviously want to update.
        // * the data is the same as the current data. Sometimes the socket will send an update for an object even
        //    though that particular object was not changed. For example, when a related object is changed and that
        //    change is reflected in the parent object. For example, counter mutations for a rental mutations.
        // * there is no current data. In that case, the ModelManager will park the data.
        // ModelManager will park the model.
        if (!oldValue || data.updatedAt!.isSameOrAfter(oldValue?.updatedAt)) {
          const instance = ModelManager.getInstance().update(modelType, data as DataType)

          if (!oldValue) {
            // In case there was no existing value to update, the instance will have been parked by the ModelManager.
            // Add it to the (related) collection, so it can be used by the client.
            // ---
            // Example: Instruction date is updated to today, but it was not in memory yet.
            // In this case, the instruction will be queued until it is retrieved explicitly, but it will be added to
            // the related order.
            // ---
            // Moreover: if this order does not exist, the onModelCreate is responsible for retrieving it first.
            // It will be parked until the order is retrieved. See useRelatedObjectsManager for more info.
            handleCreate(instance)
          }
        }
      }
    })

    socketService?.addModelDeleteHandler({
      model: modelType.className,
      handler: async (data: IBase) => {
        let instance = ModelManager.getInstance().get(data.id!)

        if (!instance) {
          // If the instance was not found in the map, check the queued updates.
          instance = ModelManager.getInstance().getQueuedUpdate(data.id!)
        }

        if (instance) {
          ModelManager.getInstance().delete(data.id!) // Mark as deleted globally.

          if (onModelDelete) {
            onModelDelete(instance as ModelType) // Call onDelete if provided.
          } else if (modelCollection) {
            // Else call splice if modelCollection is provided and object is present.
            const index = modelCollection.value.indexOf(instance)
            if (index !== -1) {
              modelCollection.value.splice(index, 1)
            }
          } else {
            throw Error("No modelCollection or onModelCreate callback provided")
          }
        }
      }
    })
  }

  function handleCreate(instance: ModelType) {
    if (onModelCreate) {
      // Call onCreate if provided.
      onModelCreate(instance as ModelType)
    } else if (modelCollection) {
      // Consecutive updates for a model that is not in the ModelManager map yet each
      // produce a fresh parked instance. Replace a previously pushed entry with the
      // same id instead of pushing a duplicate (PNTS-3077).
      const existingIndex = modelCollection.value.findIndex((model) => model.id === instance.id)
      if (existingIndex === -1) {
        modelCollection.value.push(instance)
      } else {
        modelCollection.value[existingIndex] = instance
      }
    } else {
      throw Error("No modelCollection or onModelCreate callback provided")
    }
  }

  return {
    initiate
  }
}
